import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api, supabase } from '../lib/api';
import { otpSessionIsExpired } from '../lib/otpSession';
import { isEmailConfirmationLocked } from '../lib/emailConfirmation';

/** User profile from `users` (includes `default_channel`, `preferred_dialer`, etc.). */
const AuthContext = createContext({});

const PASSWORD_RECOVERY_FLAG = 'pl_password_recovery';
const OTP_CAP_INTERVAL_MS = 5 * 60 * 1000;
/** verifyOtp fires SIGNED_IN before Login.jsx can stamp a fresh otp_login_at. */
const OTP_METADATA_WRITE_GRACE_MS = 1500;
const OTP_EXPIRED_LOGIN = '/login?error=otp_expired';
const DEACTIVATED_LOGIN = '/login?error=deactivated';
const EMAIL_CONFIRMATION_WALL = '/email-confirmation-required';

const EMAIL_CONFIRM_ALLOWLIST = new Set([
  '/email-confirmation-required',
  '/confirm-email',
  '/accept-invite',
  '/reset-password',
  '/login',
]);

/** True while the user is on the email-recovery flow. A recovery session is not a CRM login. */
export function isPasswordRecoveryMode() {
  try {
    return sessionStorage.getItem(PASSWORD_RECOVERY_FLAG) === '1';
  } catch {
    return false;
  }
}

export function setPasswordRecoveryMode(on) {
  try {
    if (on) sessionStorage.setItem(PASSWORD_RECOVERY_FLAG, '1');
    else sessionStorage.removeItem(PASSWORD_RECOVERY_FLAG);
  } catch {
    // ignore quota / private-mode failures
  }
}

function alreadyOnLoginError(errorCode) {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname === '/login' && params.get('error') === errorCode;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshGen = useRef(0);
  const otpCapGraceRef = useRef(false);
  const otpCapGraceUntilRef = useRef(0);
  const enforcingOtpCapRef = useRef(false);

  const enforceOtpSessionCap = useCallback(async ({ waitForMetadataWrite = false } = {}) => {
    if (!supabase || isPasswordRecoveryMode()) return false;

    if (!waitForMetadataWrite && Date.now() < otpCapGraceUntilRef.current) {
      return false;
    }

    const readAuthUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return null;
      const { data: { user: remote }, error } = await supabase.auth.getUser();
      if (error || !remote) return session.user;
      return remote;
    };

    let authUser = await readAuthUser();
    if (!otpSessionIsExpired(authUser)) return false;

    if (waitForMetadataWrite) {
      await new Promise((resolve) => window.setTimeout(resolve, OTP_METADATA_WRITE_GRACE_MS));
      authUser = await readAuthUser();
      if (!otpSessionIsExpired(authUser)) return false;
    }

    if (enforcingOtpCapRef.current) return true;
    enforcingOtpCapRef.current = true;
    try {
      await api.auth.signOut();
    } catch {
      // Session may already be gone.
    }
    setUser(null);

    if (typeof window !== 'undefined' && !alreadyOnLoginError('otp_expired')) {
      window.location.replace(OTP_EXPIRED_LOGIN);
      await new Promise(() => {});
    }
    return true;
  }, []);

  const rejectInactiveSession = useCallback(async () => {
    try {
      await api.auth.signOut();
    } catch {
      // Session may already be gone.
    }
    setUser(null);
    if (typeof window !== 'undefined' && !alreadyOnLoginError('deactivated')) {
      window.location.replace(DEACTIVATED_LOGIN);
      await new Promise(() => {});
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const gen = ++refreshGen.current;
    const waitForMetadataWrite = otpCapGraceRef.current;
    otpCapGraceRef.current = false;
    try {
      if (isPasswordRecoveryMode()) {
        const onResetPage =
          typeof window !== 'undefined' && window.location.pathname === '/reset-password';
        if (!onResetPage) {
          // Abandoned or completed reset — never keep a recovery session as a CRM login.
          setPasswordRecoveryMode(false);
          await api.auth.signOut();
          if (gen === refreshGen.current) setUser(null);
          return null;
        }
        if (gen === refreshGen.current) setUser(null);
        return null;
      }

      if (await enforceOtpSessionCap({ waitForMetadataWrite })) {
        if (gen === refreshGen.current) setUser(null);
        return null;
      }

      // Full users row (`select('*')`) — includes default_channel for channel defaults in the app.
      const profile = await api.auth.getCurrentUser();
      if (gen !== refreshGen.current) return profile ?? null;

      // Never write is_active from the client. Deactivated accounts are locked out.
      // Pending invites (inactive, never logged in) must keep their auth session on
      // /accept-invite so they can set a password and activate — do not sign them out.
      if (profile && profile.is_active !== true) {
        const onAcceptInvite =
          typeof window !== 'undefined' && window.location.pathname === '/accept-invite';
        const pendingInvite = !profile.first_login_at;
        if (onAcceptInvite && pendingInvite) {
          if (gen === refreshGen.current) setUser(null);
          return null;
        }
        await rejectInactiveSession();
        return null;
      }

      if (gen === refreshGen.current) setUser(profile);

      // 20-day email confirmation lockout — keep session; block app navigation.
      // Floored to feature ship date so pre-existing users are not locked out retroactively.
      if (profile && isEmailConfirmationLocked(profile)) {
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        if (!EMAIL_CONFIRM_ALLOWLIST.has(path) && typeof window !== 'undefined') {
          window.location.replace(EMAIL_CONFIRMATION_WALL);
          await new Promise(() => {});
        }
      }

      // First login detection — fire once when first_login_at is null.
      // Skip on /accept-invite: invites are active immediately, but password may not be set yet.
      const onAcceptInvite =
        typeof window !== 'undefined' && window.location.pathname === '/accept-invite';
      if (
        profile &&
        !profile.first_login_at &&
        profile.is_active === true &&
        profile.role &&
        !onAcceptInvite
      ) {
        // Mark first login and enroll in onboarding sequence
        supabase
          .from('users')
          .update({
            first_login_at: new Date().toISOString(),
            onboarding_sequence_enrolled_at: new Date().toISOString(),
          })
          .eq('id', profile.id)
          .then(() => {
            // Send Email 1 immediately — fire and forget
            supabase.auth.getSession().then(({ data: { session } }) => {
              if (!session) return;
              fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-onboarding-email`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.access_token}`,
                  },
                  body: JSON.stringify({
                    user_id: profile.id,
                    email_number: 1,
                  }),
                }
              ).catch((e) => console.error('Onboarding email 1 failed:', e));
            });
          });
      }
      return profile ?? null;
    } catch (error) {
      console.error('Error checking auth:', error);
      if (gen === refreshGen.current) setUser(null);
      return null;
    }
  }, [enforceOtpSessionCap, rejectInactiveSession]);

  useEffect(() => {
    if (!supabase) {
      refreshUser().finally(() => setLoading(false));
      return undefined;
    }

    let cancelled = false;

    const bootstrap = async () => {
      try {
        await refreshUser();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || isPasswordRecoveryMode()) {
        setPasswordRecoveryMode(true);
        setUser(null);
        return;
      }
      if (event === 'SIGNED_IN') {
        otpCapGraceRef.current = true;
        otpCapGraceUntilRef.current = Date.now() + OTP_METADATA_WRITE_GRACE_MS;
      }
      void refreshUser();
    });

    const pollCap = () => {
      void enforceOtpSessionCap();
    };
    const intervalId = window.setInterval(pollCap, OTP_CAP_INTERVAL_MS);
    const onFocus = () => pollCap();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') pollCap();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshUser, enforceOtpSessionCap]);

  const login = async (email, password) => {
    setPasswordRecoveryMode(false);
    const res = await api.auth.signIn(email, password);
    if (res?.user && res.user.is_active !== true) {
      await rejectInactiveSession();
      throw new Error('Your account has been deactivated. Contact your admin.');
    }
    if (supabase) {
      try {
        await supabase.auth.updateUser({
          data: { last_auth_method: 'password', otp_login_at: null },
        });
      } catch (metaErr) {
        console.error('Could not clear OTP session flag after password login:', metaErr);
      }
    }
    setUser(res.user);
  };

  const logout = async () => {
    await api.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
